import * as React from 'react';
import Link from 'next/link';
import { AiOutlineClockCircle } from 'react-icons/ai';
import { PRICING_PAGE } from 'constants/pages';
import { useMarketClockMs } from 'hooks/useMarketSessionClock';
import { trackEvent } from 'services/client/analytics';
import { CLICK_UPGRADE } from 'services/client/analytics/events';
import classNames from 'styles/utils/classNames';
import { DELAYED_FLOW_CTA, delayedFlowNoticeCopy } from './delayedFlowNoticeModel';

/**
 * Both tables scroll sideways: their bodies are as wide as every column (about 1,000px for the
 * market table), so text centered across the body lands off-screen on a phone. Capping the notice
 * at the viewport keeps it inside the part of the table a reader sees before scrolling.
 */
const FIT_VISIBLE_WIDTH = 'w-full max-w-[calc(100vw-2.5rem)]';

interface Props {
  /** The ET trading date of the empty free session, or null when the worker has no session yet. */
  sessionDate: string | null;
  /** `panel` fills the empty flow table. `line` is one centered sentence for the market table. */
  variant?: 'panel' | 'line';
}

/**
 * Explains an empty free feed once today's trading has started, so the table does not look broken.
 *
 * The caller passes the session date of a delayed feed that is connected and empty. This component
 * checks that the date is today, picks the headline for the session, and renders nothing otherwise.
 */
export default function DelayedFlowNotice({ sessionDate, variant = 'panel' }: Props) {
  // The shared session clock: null on the server and before mount, so the markup stays clock-free.
  const nowMs = useMarketClockMs();
  const copy = nowMs === null ? null : delayedFlowNoticeCopy(nowMs, sessionDate);

  if (!copy) return null;

  const trackClick = () =>
    trackEvent(CLICK_UPGRADE, {
      type: 'pricing',
      surface: 'delayed_flow_notice',
      placement: variant === 'line' ? 'market_table' : 'flow_table',
    });

  if (variant === 'line') {
    return (
      <div
        data-delayed-flow-notice="line"
        className={classNames(
          FIT_VISIBLE_WIDTH,
          'flex h-full items-center justify-center px-3 py-3',
        )}
      >
        <p className="text-center text-xs leading-relaxed text-text-secondary">
          {copy.marketLine}{' '}
          <Link
            href={PRICING_PAGE}
            onClick={trackClick}
            className="whitespace-nowrap font-semibold text-white underline"
          >
            {DELAYED_FLOW_CTA}
          </Link>
        </p>
      </div>
    );
  }

  return (
    <div
      data-delayed-flow-notice="panel"
      className={classNames(FIT_VISIBLE_WIDTH, 'flex justify-center px-4 pb-6 pt-6 md:pt-10')}
    >
      <div className="flex max-w-md flex-col items-center text-center">
        <AiOutlineClockCircle aria-hidden="true" className="h-6 w-6 text-text-secondary" />
        <p className="mt-3 text-pretty font-space text-[15px] font-bold leading-snug text-white">
          {copy.headline}
        </p>
        <p className="mt-1.5 text-pretty text-xs leading-relaxed text-text-secondary">
          {copy.detail}
        </p>
        <Link
          href={PRICING_PAGE}
          onClick={trackClick}
          className="mt-4 inline-flex items-center rounded bg-brand-primary px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-brand-primary/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
        >
          {DELAYED_FLOW_CTA}
        </Link>
      </div>
    </div>
  );
}
